import type { DefaultDtoType, Field } from "../Field";
import { initField } from "../Field";
import type { InferOutput } from "valibot";
import { object, string } from "valibot";
import { dataName, label, optionalTextValue, rememberInput, required } from "../../utils/validation/base";
import { useTranslation } from "../../state/i18n";
import { TextInput } from "../../components/inputs/TextInput";
import { useFormServices } from "../../services/formServices";
import { MAX_CHARACTERS_SM, MAX_CHARACTERS_XS, TEXT_CONDITION_KEYS } from "../../constants";
import { defaultStringFilter } from "../../pages/customer/form/filter";
import { InputPreview, PreviewContentLabel } from "../FieldPreviewComponents";
import PropertySection from "../PropertySection";
import { Toggle } from "../../components/toggle/Toggle";
import { keyFromSelector } from "i18next";

const Schema = object({
  label_text: label(),
  data_name: dataName(),
  text_placeholder: optionalTextValue(MAX_CHARACTERS_SM),
  text_default_value: optionalTextValue(MAX_CHARACTERS_XS),
  required: required(),
  remember_input: rememberInput(),
});
const ValueSchema = string();

export type PhoneFieldProperties = InferOutput<typeof Schema>;
type ValueType = InferOutput<typeof ValueSchema>;

const PhoneField: Field<PhoneFieldProperties, DefaultDtoType, ValueType> = {
  type: "phone",
  category: "personalInfo",
  widget: "com.moreapps:phone:1",
  schema: Schema,
  valueSchema: ValueSchema,
  hasData: true,
  defaultProperties: () => {
    const { t } = useTranslation();
    return {
      text_placeholder: t(($) => $.PHONE_FIELD_PLACEHOLDER_DEFAULT),
      text_default_value: "",
      required: false,
      remember_input: false,
    };
  },
  ruleBehaviour: {
    conditions: { keys: TEXT_CONDITION_KEYS, variant: "text" },
    actions: {
      variant: "text",
      setValue: { fieldTypes: ["lookup", "phone", "radio", "search", "text", "textArea"], variableTypes: ["string"] },
    },
  },
  filter: {
    type: "string",
    definition: defaultStringFilter,
  },
  stringValue: (value) => value,
  renderValue: (value) => value,
  icon: "phone",
  titleKey: keyFromSelector(($) => $.PHONE_FIELD_LABEL),
  descriptionKey: keyFromSelector(($) => $.PHONE_FIELD_DESCRIPTION),
  propertyKeys: {
    label_text: keyFromSelector(($) => $.FIELD_PROPERTY_LABEL_TEXT),
    text_placeholder: keyFromSelector(($) => $.FIELD_PROPERTY_PLACEHOLDER),
    text_default_value: keyFromSelector(($) => $.FIELD_PROPERTY_DEFAULT_VALUE),
    data_name: keyFromSelector(($) => $.FIELD_PROPERTY_DATA_NAME),
    required: keyFromSelector(($) => $.FIELD_PROPERTY_REQUIRED),
    remember_input: keyFromSelector(($) => $.FIELD_PROPERTY_REMEMBER_INPUT),
  },
  PreviewContent: (props) => (
    <PreviewContentLabel uid={props.uid} label={props.properties.label_text} required={props.properties.required}>
      <InputPreview placeholder={props.properties.text_placeholder} value={props.properties.text_default_value} />
    </PreviewContentLabel>
  ),
  EditItem: (props) => {
    const { t } = useTranslation();
    const { fieldService } = useFormServices();

    return (
      <>
        <PropertySection>
          <TextInput
            label={t(props.propertyKeys.label_text)}
            value={props.data.properties.label_text}
            onBlur={(e) => fieldService.setLabelProperty(props.uid, e.target.value)}
            errorMessage={props.getError("label_text")}
            required
          />
          <TextInput
            label={t(props.propertyKeys.text_placeholder)}
            value={props.data.properties.text_placeholder!}
            onBlur={(e) => props.setValue("text_placeholder", e.target.value)}
            errorMessage={props.getError("text_placeholder")}
          />
          <TextInput
            label={t(props.propertyKeys.text_default_value)}
            value={props.data.properties.text_default_value!}
            onBlur={(e) => props.setValue("text_default_value", e.target.value)}
            errorMessage={props.getError("text_default_value")}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_VALIDATION)}>
          <Toggle
            block
            label={t(($) => $.REQUIRED_SWITCH)}
            checked={props.data.properties.required}
            setChecked={(checked) => props.setValue("required", checked)}
            description={t(($) => $.FIELD_PROPERTY_REQUIRED_DESCRIPTION)}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_BEHAVIOUR)}>
          <Toggle
            block
            label={t(($) => $.REMEMBER_INPUT_SWITCH)}
            checked={props.data.properties.remember_input}
            setChecked={(checked) => props.setValue("remember_input", checked)}
            description={t(($) => $.FIELD_PROPERTY_REMEMBER_INPUT_DESCRIPTION)}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_ADVANCED)}>
          <TextInput
            label={t(props.propertyKeys.data_name)}
            value={props.data.properties.data_name}
            onBlur={(e) => props.setValue("data_name", e.target.value)}
            required
            errorMessage={props.getError("data_name")}
            info={{
              title: t(props.propertyKeys.data_name),
              alt: t(($) => $.FIELD_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.data_name }),
              description: t(($) => $.FIELD_PROPERTY_DATA_NAME_DESCRIPTION),
            }}
          />
        </PropertySection>
      </>
    );
  },
};

export default initField(PhoneField);
